import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import Modal from '../components/ui/Modal';

// Types for different modal configurations
type ModalVariant = 'danger' | 'warning' | 'success' | 'info';

interface ConfirmationOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ModalVariant;
}

interface AlertOptions {
  title?: string;
  message: string;
  variant?: ModalVariant;
  buttonLabel?: string;
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions) => Promise<boolean>;
  alert: (options: AlertOptions) => Promise<void>;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

// Hook to use the confirmation context
export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (context === undefined) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
};

interface ConfirmationProviderProps {
  children: ReactNode;
}

// Internal state for the modal
interface ModalState {
  isOpen: boolean;
  type: 'confirm' | 'alert';
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  buttonLabel: string;
  variant: ModalVariant;
  resolve: ((value: boolean) => void) | null;
}

export const ConfirmationProvider: React.FC<ConfirmationProviderProps> = ({ children }) => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    type: 'confirm',
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    buttonLabel: 'OK',
    variant: 'warning',
    resolve: null,
  });

  const confirm = useCallback((options: ConfirmationOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setModalState({
        isOpen: true,
        type: 'confirm',
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        buttonLabel: 'OK',
        variant: options.variant || 'warning',
        resolve,
      });
    });
  }, []);

  const alert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      setModalState({
        isOpen: true,
        type: 'alert',
        title: options.title || (options.variant === 'success' ? 'Success' : options.variant === 'danger' ? 'Error' : 'Notice'),
        message: options.message,
        confirmLabel: 'OK',
        cancelLabel: 'Cancel',
        buttonLabel: options.buttonLabel || 'OK',
        variant: options.variant || 'info',
        resolve: () => resolve(),
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (modalState.resolve) {
      modalState.resolve(true);
    }
    setModalState((prev) => ({ ...prev, isOpen: false, resolve: null }));
  }, [modalState.resolve]);

  const handleCancel = useCallback(() => {
    if (modalState.resolve) {
      modalState.resolve(false);
    }
    setModalState((prev) => ({ ...prev, isOpen: false, resolve: null }));
  }, [modalState.resolve]);

  const getVariantStyles = (variant: ModalVariant) => {
    switch (variant) {
      case 'danger':
        return {
          icon: AlertCircle,
          iconBgColor: 'bg-red-100',
          iconColor: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          buttonColor: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          iconBgColor: 'bg-amber-100',
          iconColor: 'text-amber-600',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          buttonColor: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
        };
      case 'success':
        return {
          icon: CheckCircle,
          iconBgColor: 'bg-green-100',
          iconColor: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          buttonColor: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
        };
      case 'info':
        return {
          icon: Info,
          iconBgColor: 'bg-blue-100',
          iconColor: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          buttonColor: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
        };
    }
  };

  const styles = getVariantStyles(modalState.variant);
  const Icon = styles.icon;

  return (
    <ConfirmationContext.Provider value={{ confirm, alert }}>
      {children}

      {/* Confirmation/Alert Modal - Using Headless UI */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={handleCancel}
        size="md"
        zIndex="z-[9999]"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${styles.iconBgColor} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${styles.iconColor}`} />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{modalState.title}</h2>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <div className={`p-4 ${styles.bgColor} border ${styles.borderColor} rounded-xl`}>
            <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{modalState.message}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
          {modalState.type === 'confirm' && (
            <button
              onClick={handleCancel}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              {modalState.cancelLabel}
            </button>
          )}
          <button
            onClick={handleConfirm}
            autoFocus
            className={`px-4 py-2.5 text-sm font-medium text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${styles.buttonColor}`}
          >
            {modalState.type === 'confirm' ? modalState.confirmLabel : modalState.buttonLabel}
          </button>
        </div>
      </Modal>
    </ConfirmationContext.Provider>
  );
};

export default ConfirmationProvider;
